import React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  InfoOutlined as InfoIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import { Controller } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import CustomTextField from "components/Form/CustomTextField";
import FormField from "layouts/applications/wizard/components/FormField";
import { useTheme } from "@mui/material/styles";
import isEmpty from "lodash/isEmpty";

const ShiftJobSettingsModal = ({
  open,
  onClose,
  onSave,
  control,
  watch,
  getValues,
  setValue,
  trigger,
  locationList = [],
  errors = {},
}) => {
  const theme = useTheme();

  const handleSave = async () => {
    try {
      // const isValid = await trigger();
      // if (isValid) {
        if (onSave) {
          await onSave();
        }
      // }
    } catch (error) {
      console.error("Error saving shift job settings:", error);
    }
  };

  const switchRowData = [
    {
      left: {
        label: "Allow Employee to Edit Time",
        name: "additionalConfig.allowManualPunches",
        tooltip: "Allow employees to manually edit their time entries",
      },
      right: null,
    },
    {
      left: {
        label: "Geofence",
        name: "additionalConfig.geofence",
        tooltip: "Require employees to be within specified location to clock in/out",
      },
      right: null,
    },
    {
      left: {
        label: "Allow Scheduling Unaccrued PTO",
        name: "additionalConfig.allowUnaccruedLeaveRequest",
        disabled: true,
        tooltip: "Allow scheduling of PTO that hasn't been accrued yet",
      },
      right: null,
    },
    {
      left: {
        label: "Allow Auto Clockout",
        name: "additionalConfig.autoClockoutShiftEnd",
        tooltip: "Automatically clock out employees at shift end time",
      },
      right: null,
    },
    {
      left: {
        label: "Allow Negative Leave",
        name: "additionalConfig.allowNegativeLeave",
        disabled: true,
        tooltip: "Allow employees to go into negative leave balance",
      },
      right: null,
    },
    {
      left: {
        label: "Allow Overtime",
        name: "additionalConfig.allowOvertime",
        tooltip: "Allow overtime calculations for this shift",
      },
      right: null,
    },
  ];

  const renderSwitchField = (item, isLast = false) => (
    <Controller
      name={item.name}
      control={control}
      render={({ field }) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            py: 1.5,
            px: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: item.disabled ? theme.palette.text.disabled : theme.palette.text.primary,
                fontSize: "0.875rem",
                fontWeight: 400,
              }}
            >
              {item.label}
            </Typography>
            <Tooltip title={item.tooltip} placement="top">
              <InfoIcon
                sx={{
                  fontSize: 20,
                  color: item.disabled ? theme.palette.text.disabled : "lightgreen",
                  cursor: "help",
                }}
              />
            </Tooltip>
          </Box>
          <Switch
            {...field}
            checked={field.value || false}
            onChange={(e) => field.onChange(e.target.checked)}
            disabled={item.disabled}
            size="medium"
            sx={{
              "& .MuiSwitch-switchBase": {
                "&.Mui-checked": {
                  color: "#fff",
                  "& + .MuiSwitch-track": {
                    backgroundColor: theme.palette.success.main,
                    opacity: 1,
                  },
                },
                "&.Mui-disabled": {
                  color: theme.palette.action.disabled,
                  "& + .MuiSwitch-track": {
                    backgroundColor: theme.palette.action.disabledBackground,
                    opacity: 0.5,
                  },
                },
              },
              "& .MuiSwitch-track": {
                backgroundColor: theme.palette.grey[400],
                opacity: 1,
              },
            }}
          />
        </Box>
      )}
    />
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: 500,
          maxWidth: 850,
          paddingX: 3,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 2,
          pt: 3,
          px: 3,
        }}
      >
        <Typography variant="h6" component="div" sx={{ fontWeight: 600, fontSize: "1.25rem" }}>
          Shift Job Settings
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: theme.palette.text.secondary,
            backgroundColor: theme.palette.grey[200],
            "&:hover": {
              backgroundColor: theme.palette.grey[300],
            },
            width: 32,
            height: 32,
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 4, py: 3 }}>
        <Stack spacing={4}>
          {/* Info Alert */}
          {/* <Alert
            severity="info"
            sx={{
              backgroundColor: "#e3f2fd",
              color: "#1976d2",
              border: "1px solid #bbdefb",
              borderRadius: 1,
              "& .MuiAlert-icon": {
                color: "#1976d2",
              },
            }}
          >
            Lorem ipsum dolor sit amet et delectus
          </Alert> */}

          {/* Top Row with Location, Early Clock in Minutes, and Clock-in At Shift Start */}
          <Grid container spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <Controller
                name="location"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.primary,
                        fontSize: "0.875rem",
                        mb: 1.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      Location
                      <Tooltip title="Select job location" placement="top">
                        <InfoIcon sx={{ fontSize: 20, color: "lightgreen" }} />
                      </Tooltip>
                    </Typography>
                    <Autocomplete
                      options={!isEmpty(locationList) ? locationList : []}
                      getOptionLabel={(option) => option.locationName || ""}
                      value={getValues("location") ? field.value : null}
                      onChange={(e, data) => field.onChange(data)}
                      renderInput={(params) => (
                        <CustomTextField
                          {...params}
                          placeholder=""
                          showError
                          errorMessage={error?.message}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 1,
                              height: 48,
                            },
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li
                          {...props}
                          style={{
                            color:
                              option.primaryLocation === "Yes"
                                ? theme.palette.info.main
                                : "inherit",
                            fontWeight: option.primaryLocation === "Yes" ? "bold" : "normal",
                          }}
                        >
                          {option.locationName}
                        </li>
                      )}
                    />
                  </Box>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Controller
                name="additionalConfig.earlyClockInMinutes"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.primary,
                        fontSize: "0.875rem",
                        mb: 1.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      Early Clock in Minutes
                      <Tooltip
                        title="Minutes before shift start that employees can clock in"
                        placement="top"
                      >
                        <InfoIcon sx={{ fontSize: 20, color: "lightgreen" }} />
                      </Tooltip>
                    </Typography>
                    <NumericFormat
                      {...field}
                      InputProps={{
                        inputProps: { inputMode: "numeric" },
                      }}
                      allowNegative={false}
                      suffix=" Minutes"
                      placeholder="0 Minutes"
                      decimalScale={0}
                      customInput={FormField}
                      showError
                      errorMessage={error?.message}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 1,
                          height: 48,
                        },
                      }}
                    />
                  </Box>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    mb: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  Clock-in At Shift Start
                  <Tooltip
                    title="Automatically adjust clock-in time to shift start"
                    placement="top"
                  >
                    <InfoIcon sx={{ fontSize: 20, color: "lightgreen" }} />
                  </Tooltip>
                </Typography>
                <Controller
                  name="additionalConfig.autoAdjustEarlyClockIn"
                  control={control}
                  render={({ field }) => (
                    <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 1 }}>
                      <Switch
                        {...field}
                        checked={field.value || false}
                        onChange={(e) => field.onChange(e.target.checked)}
                        disabled={watch("additionalConfig.earlyClockInMinutes") < 1}
                        size="medium"
                        sx={{
                          "& .MuiSwitch-switchBase": {
                            "&.Mui-checked": {
                              color: "#fff",
                              "& + .MuiSwitch-track": {
                                backgroundColor: theme.palette.success.main,
                                opacity: 1,
                              },
                            },
                            "&.Mui-disabled": {
                              color: theme.palette.action.disabled,
                              "& + .MuiSwitch-track": {
                                backgroundColor: theme.palette.action.disabledBackground,
                                opacity: 0.5,
                              },
                            },
                          },
                          "& .MuiSwitch-track": {
                            backgroundColor: theme.palette.grey[400],
                            opacity: 1,
                          },
                        }}
                      />
                    </Box>
                  )}
                />
              </Box>
            </Grid>
          </Grid>

          {/* Switch Controls - Each in its own row with divider lines */}
          <Box
            sx={{
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            {switchRowData.map((row, index) => (
              <Box key={row.left?.toString()}>
                {renderSwitchField(row.left, index === switchRowData.length - 1)}
              </Box>
            ))}
          </Box>

          {/* Conditional Negative Hours Limit Field */}
          {watch("additionalConfig.allowNegativeLeave") && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="additionalConfig.negativeHoursLimit"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <NumericFormat
                      {...field}
                      InputProps={{
                        inputProps: { inputMode: "numeric" },
                      }}
                      allowNegative={false}
                      suffix=" Hours"
                      label="Negative Hours Limit"
                      decimalScale={0}
                      customInput={FormField}
                      disabled={!watch("additionalConfig.allowNegativeLeave")}
                      showError
                      errorMessage={error?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
          )}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 3,
          gap: 2,
          justifyContent: "flex-end",
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: "#d32f2f",
            color: "white",
            "&:hover": {
              backgroundColor: "#b71c1c",
            },
            minWidth: 100,
            textTransform: "uppercase",
            fontWeight: 600,
            borderRadius: 1,
            px: 3,
            py: 1,
          }}
        >
          Close
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            backgroundColor: "#1976d2",
            color: "white",
            "&:hover": {
              backgroundColor: "#1565c0",
            },
            minWidth: 100,
            textTransform: "uppercase",
            fontWeight: 600,
            borderRadius: 1,
            px: 3,
            py: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          Save
          <LockIcon sx={{ fontSize: 16 }} />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShiftJobSettingsModal;
